<script setup lang="ts">
/**
 * "บริการล่ม 2 รายการ · ดูหน้า Health" — a thin strip across the top of every page (not /status itself) for
 * owner/head while anything is down (fail twice in a row). It pushes the page down instead of covering it
 * (html.cf-banner, rules below). "ซ่อน" hides it until the set of services that are down changes.
 * Polls every 60 s through stores/health.ts (nothing for staff).
 */
import { computed, onBeforeUnmount, onMounted, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import { downNames } from '../../lib/health'
import { useHealth } from '../../stores/health'
import Icon from '../ui/Icon.vue'

const health = useHealth()
const route = useRoute()

onMounted(() => health.start())
onBeforeUnmount(() => {
  health.stop()
  document.documentElement.classList.remove('cf-banner')
})

const show = computed(() => health.showBanner && route.name !== 'health')
const count = computed(() => health.summary?.down ?? 0)
const names = computed(() => (health.summary ? downNames(health.summary) : ''))

watchEffect(() => {
  if (typeof document !== 'undefined') document.documentElement.classList.toggle('cf-banner', show.value)
})
</script>

<template>
  <div v-if="show" class="hbn" role="status" aria-live="polite">
    <span class="hbn-ic"><Icon name="pulse" /></span>
    <b class="hbn-t">บริการล่ม {{ count }} รายการ</b>
    <span v-if="names" class="hbn-n">{{ names }}</span>
    <RouterLink class="hbn-go" :to="{ name: 'health' }">ดูหน้า Health<Icon name="arrowRight" /></RouterLink>
    <button type="button" class="hbn-x" aria-label="ซ่อนแถบนี้จนกว่ารายการที่ล่มจะเปลี่ยน" title="ซ่อน" @click="health.dismissBanner()">
      <Icon name="x" />
    </button>
  </div>
</template>

<style>
/* the strip takes 36 px at the top: every page's root (.app, 100vh) shrinks by that much */
html.cf-banner #app {
  padding-top: 36px;
}
html.cf-banner #app .app {
  height: calc(100vh - 36px);
  min-height: 604px;
}
html.cf-banner .aha {
  top: 52px;
}
</style>

<style scoped>
.hbn {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 55;
  height: 36px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px 0 20px;
  background: var(--crits);
  border-bottom: 1px solid #f1c7c1;
  color: #7f1d14;
  font-size: 13px;
  line-height: 20px;
  white-space: nowrap;
}
.hbn-ic {
  flex: none;
  display: inline-flex;
  color: var(--crit);
}
.hbn-ic :deep(.ic) {
  width: 17px;
  height: 17px;
}
.hbn-t {
  flex: none;
  font-weight: 600;
  color: #a61b12;
}
.hbn-n {
  min-width: 0;
  overflow: hidden;
  color: #8f3a31;
}
.hbn-n::before {
  content: '·';
  margin-right: 10px;
  color: #d19a93;
}
.hbn-go {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  height: 26px;
  padding: 0 10px;
  border-radius: 7px;
  background: #fff;
  border: 1px solid #f1c7c1;
  color: #a61b12;
  font-weight: 600;
  text-decoration: none;
}
.hbn-go:hover {
  background: #fff7f6;
}
.hbn-go :deep(.ic) {
  width: 14px;
  height: 14px;
}
.hbn-x {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 7px;
  color: #8f3a31;
}
.hbn-x:hover {
  background: #fad9d5;
}
.hbn-x :deep(.ic) {
  width: 16px;
  height: 16px;
}
</style>
